// Product Image Generator API Service

import { apiFetch } from '../../lib/apiFetch.js';

const API_BASE_URL = 'https://ah7mrbv78e.execute-api.ap-south-1.amazonaws.com';

export const productImageGenApi = {
  createRunId(username) {
    const user = (username || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
    const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 15);
    return `${user}_${ts}`;
  },

  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  // Brand endpoints
  async listBrands() {
    const res = await apiFetch(`${API_BASE_URL}/api/brands`);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  },

  async createBrand(brandName, guidelinesText, brandWebsite, referenceUrl, logoImages) {
    const logoB64 = [];
    if (logoImages) {
      for (const file of logoImages) {
        logoB64.push(await this.fileToBase64(file));
      }
    }
    const res = await apiFetch(`${API_BASE_URL}/api/brands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        brand_name: brandName,
        guidelines_text: guidelinesText,
        brand_website: brandWebsite,
        reference_url: referenceUrl,
        logo_images: logoB64,
      })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Failed: ${res.status}`);
    }
    return res.json();
  },

  async deleteBrand(brandId) {
    const res = await apiFetch(`${API_BASE_URL}/api/brands/${brandId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  },

  // Generate endpoint (async — returns immediately, poll for status)
  async generateImages(brandId, files, category, runId, user, onProgress) {
    const total = files.length;
    if (onProgress) onProgress({ completed: 0, total, phase: 'uploading' });

    // Send all images in one request
    const images = [];
    for (const file of files) {
      images.push({ name: file.name, data: await this.fileToBase64(file) });
    }

    const res = await apiFetch(`${API_BASE_URL}/api/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ brand_id: brandId, run_id: runId, category, images, user })
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Generation failed: ${res.status}`);
    }

    // Poll for completion
    if (onProgress) onProgress({ completed: 0, total, phase: 'generating' });

    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max (5s intervals)
    while (attempts < maxAttempts) {
      await new Promise(r => setTimeout(r, 5000));
      attempts++;

      try {
        const jobRes = await apiFetch(`${API_BASE_URL}/api/jobs/${runId}`);
        if (!jobRes.ok) continue;
        const jobData = await jobRes.json();
        const job = jobData.job;

        if (job?.status === 'completed') {
          if (onProgress) onProgress({ completed: total, total, phase: 'done' });
          // Fix view URLs
          if (job.results) {
            for (const r of job.results) {
              for (const v of (r.variants || [])) {
                if (v.view_url?.startsWith('/')) v.view_url = `${API_BASE_URL}${v.view_url}`;
              }
            }
          }
          return job;
        }

        if (job?.status === 'failed') {
          throw new Error(job.error || 'Generation failed');
        }

        // Still processing
        const generated = job?.total_generated || 0;
        if (onProgress) onProgress({ completed: generated, total: total * 6, phase: 'generating' });
      } catch (pollErr) {
        if (pollErr.message.includes('Generation failed')) throw pollErr;
        // Network error during poll — keep trying
      }
    }

    throw new Error('Generation timed out — check History tab for results');
  },

  // Job history
  async listJobs() {
    const res = await apiFetch(`${API_BASE_URL}/api/jobs`);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  },

  async getJob(runId) {
    const res = await apiFetch(`${API_BASE_URL}/api/jobs/${runId}`);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  },
};
